import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { tradeIdeasBotApi } from "@/lib/tradeideas/client";
import type { TradeIdeasColumnPatch, TradeIdeasSink } from "@/lib/tradeideas/types";

export const tradeIdeasKeys = {
  all: ["tradeideas-bot"] as const,
  health: () => [...tradeIdeasKeys.all, "health"] as const,
  status: () => [...tradeIdeasKeys.all, "status"] as const,
  config: () => [...tradeIdeasKeys.all, "config"] as const,
  scanners: () => [...tradeIdeasKeys.all, "scanners"] as const,
  filters: () => [...tradeIdeasKeys.all, "filters"] as const,
  filterCatalogSeed: () => [...tradeIdeasKeys.all, "filter-catalog-seed"] as const,
  columns: (scannerId: string) =>
    [...tradeIdeasKeys.all, "columns", scannerId] as const,
  columnMappings: (scannerId: string) =>
    [...tradeIdeasKeys.all, "column-mappings", scannerId] as const,
  sinks: () => [...tradeIdeasKeys.all, "sinks"] as const,
  outbox: (status: string) => [...tradeIdeasKeys.all, "outbox", status] as const,
};

export function useTradeIdeasStatus() {
  return useQuery({
    queryKey: tradeIdeasKeys.status(),
    queryFn: tradeIdeasBotApi.status,
    refetchInterval: 10_000,
  });
}

export function useTradeIdeasHealth() {
  return useQuery({
    queryKey: tradeIdeasKeys.health(),
    queryFn: tradeIdeasBotApi.health,
    refetchInterval: 10_000,
  });
}

export function useTradeIdeasConfig() {
  return useQuery({
    queryKey: tradeIdeasKeys.config(),
    queryFn: tradeIdeasBotApi.config,
    refetchInterval: 30_000,
  });
}

export function useTradeIdeasScanners() {
  return useQuery({
    queryKey: tradeIdeasKeys.scanners(),
    queryFn: tradeIdeasBotApi.scanners,
    refetchInterval: 15_000,
  });
}

export function useTradeIdeasFilters() {
  return useQuery({
    queryKey: tradeIdeasKeys.filters(),
    queryFn: tradeIdeasBotApi.filters,
    staleTime: 60_000,
  });
}

export function useTradeIdeasFilterCatalogSeed() {
  return useQuery({
    queryKey: tradeIdeasKeys.filterCatalogSeed(),
    queryFn: tradeIdeasBotApi.filterCatalogSeed,
    staleTime: 60_000,
  });
}

export function useRefreshTradeIdeasFilterCatalog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: tradeIdeasBotApi.refreshFilterCatalog,
    onSuccess: (result) => {
      toast.success(`Refreshed ${result.imported} catalog filters`);
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.filters() });
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.filterCatalogSeed() });
    },
    onError: (err) => toast.error(`Catalog refresh failed: ${(err as Error).message}`),
  });
}

export function useTradeIdeasColumns(scannerId: string | null) {
  return useQuery({
    queryKey: scannerId ? tradeIdeasKeys.columns(scannerId) : tradeIdeasKeys.columns(""),
    queryFn: () => tradeIdeasBotApi.columns(scannerId ?? ""),
    enabled: Boolean(scannerId),
    refetchInterval: 20_000,
  });
}

export function useTradeIdeasColumnMappings(scannerId: string | null) {
  return useQuery({
    queryKey: scannerId
      ? tradeIdeasKeys.columnMappings(scannerId)
      : tradeIdeasKeys.columnMappings(""),
    queryFn: () => tradeIdeasBotApi.columnMappings(scannerId ?? ""),
    enabled: Boolean(scannerId),
    refetchInterval: 20_000,
  });
}

export function useUpdateTradeIdeasColumn(scannerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ columnId, patch }: { columnId: number; patch: TradeIdeasColumnPatch }) =>
      tradeIdeasBotApi.updateColumn(scannerId, columnId, patch),
    onSuccess: () => {
      toast.success("Column mapping updated");
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.columns(scannerId) });
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.scanners() });
    },
    onError: (err) => toast.error(`Column update failed: ${(err as Error).message}`),
  });
}

export function useUpdateTradeIdeasColumnMapping(scannerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ columnId, patch }: { columnId: number; patch: TradeIdeasColumnPatch }) =>
      tradeIdeasBotApi.updateColumnMapping(scannerId, columnId, patch),
    onSuccess: () => {
      toast.success("Column mapping updated");
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.columnMappings(scannerId) });
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.columns(scannerId) });
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.scanners() });
    },
    onError: (err) => toast.error(`Column update failed: ${(err as Error).message}`),
  });
}

export function useTradeIdeasSinks() {
  return useQuery({
    queryKey: tradeIdeasKeys.sinks(),
    queryFn: tradeIdeasBotApi.sinks,
    refetchInterval: 30_000,
  });
}

export function useUpsertTradeIdeasSink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, sink }: { name: string; sink: TradeIdeasSink }) =>
      tradeIdeasBotApi.upsertSink(name, sink),
    onSuccess: () => {
      toast.success("Delivery sink saved");
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.sinks() });
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.config() });
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.status() });
    },
    onError: (err) => toast.error(`Sink save failed: ${(err as Error).message}`),
  });
}

export function useDeleteTradeIdeasSink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => tradeIdeasBotApi.deleteSink(name),
    onSuccess: () => {
      toast.success("Delivery sink deleted");
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.sinks() });
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.config() });
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.status() });
    },
    onError: (err) => toast.error(`Sink delete failed: ${(err as Error).message}`),
  });
}

export function useTradeIdeasOutbox(status: string) {
  return useQuery({
    queryKey: tradeIdeasKeys.outbox(status),
    queryFn: () => tradeIdeasBotApi.outbox(status),
    refetchInterval: 15_000,
  });
}

export function useRetryTradeIdeasOutbox() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: tradeIdeasBotApi.retryOutbox,
    onSuccess: (result) => {
      toast.success(`Queued ${result.queued} deliveries`);
      qc.invalidateQueries({ queryKey: tradeIdeasKeys.all });
    },
    onError: (err) => toast.error(`Outbox retry failed: ${(err as Error).message}`),
  });
}
